import React, { useMemo, forwardRef } from "react";
import {
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Line,
  ReferenceLine,
  ReferenceArea,
  Label,
  ResponsiveContainer,
} from "recharts";
import { ZONE_COLORS } from "../lib/constants";
import { polynomialRegression } from "../lib/polyfit";

/**
 * LactateChart
 * Dual-axis chart: Blood Lactate (mmol/L) left, Heart Rate (bpm) right, vs Speed (x).
 * Zone ReferenceAreas stacked by speed; LT1/LT2 drawn as vertical dashed lines.
 * A faint 3rd-degree polynomial trend line is drawn BEHIND the lactate curve.
 */
function parseRange(str) {
  if (!str || typeof str !== "string") return null;
  const cleaned = str.replace(/\s/g, "").replace(/–|—/g, "-");
  if (cleaned.startsWith("<")) {
    const v = parseFloat(cleaned.slice(1));
    return Number.isFinite(v) ? { min: null, max: v } : null;
  }
  if (cleaned.endsWith("+")) {
    const v = parseFloat(cleaned.slice(0, -1));
    return Number.isFinite(v) ? { min: v, max: null } : null;
  }
  const parts = cleaned.split("-").filter(Boolean);
  if (parts.length === 1) {
    const v = parseFloat(parts[0]);
    return Number.isFinite(v) ? { min: v, max: v } : null;
  }
  const min = parseFloat(parts[0]);
  const max = parseFloat(parts[1]);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  return { min, max };
}

const LactateChart = forwardRef(function LactateChart(
  { test, height = 460 },
  ref
) {
  const { rows = [], lt1 = {}, lt2 = {}, zones = [], speed_unit = "mph" } = test || {};

  const data = useMemo(() => {
    return rows
      .filter((r) => r.speed != null && r.speed !== "")
      .map((r) => ({
        speed: Number(r.speed),
        lactate: r.blood_lactate != null && r.blood_lactate !== "" ? Number(r.blood_lactate) : null,
        hr: r.hr != null && r.hr !== "" ? Number(r.hr) : null,
      }))
      .sort((a, b) => a.speed - b.speed);
  }, [rows]);

  const speedDomain = useMemo(() => {
    const speeds = data.map((d) => d.speed).filter((v) => Number.isFinite(v));
    if (speeds.length === 0) return [0, 10];
    const min = Math.min(...speeds);
    const max = Math.max(...speeds);
    const pad = Math.max(0.3, (max - min) * 0.08);
    return [Math.max(0, +(min - pad).toFixed(2)), +(max + pad).toFixed(2)];
  }, [data]);

  // 3rd-degree polynomial fit on lactate points → generate a smooth trend curve
  const merged = useMemo(() => {
    const lactatePts = data
      .filter((d) => d.lactate != null)
      .map((d) => ({ x: d.speed, y: d.lactate }));
    const fit = polynomialRegression(lactatePts, 3);
    if (!fit) return data.map((d) => ({ ...d, lactate_fit: null }));
    // sample N points across the domain, clamp predictions to >= 0
    const [dMin, dMax] = speedDomain;
    const samples = 64;
    const fitPoints = [];
    for (let i = 0; i <= samples; i++) {
      const x = dMin + ((dMax - dMin) * i) / samples;
      const y = Math.max(0, fit.predict(x));
      fitPoints.push({ speed: x, lactate_fit: y });
    }
    // Merge raw + fit; raw points get lactate_fit = null (fit already has its own samples)
    const raw = data.map((d) => ({ ...d, lactate_fit: null }));
    const combined = [...raw, ...fitPoints];
    combined.sort((a, b) => a.speed - b.speed);
    return combined;
  }, [data, speedDomain]);

  const zoneBands = useMemo(() => {
    const [dMin, dMax] = speedDomain;
    return zones
      .map((z, idx) => {
        const range = parseRange(z.speed_range);
        if (!range) return null;
        const min = range.min == null ? dMin : Math.max(range.min, dMin);
        const max = range.max == null ? dMax : Math.min(range.max, dMax);
        if (max <= min) return null;
        return {
          key: `${idx}-${z.color_key}`,
          x1: min,
          x2: max,
          color: ZONE_COLORS[z.color_key] || "#EEE",
          label: z.zone_label,
        };
      })
      .filter(Boolean);
  }, [zones, speedDomain]);

  const lt1Speed = lt1?.speed !== "" && lt1?.speed != null ? Number(lt1.speed) : null;
  const lt2Speed = lt2?.speed !== "" && lt2?.speed != null ? Number(lt2.speed) : null;

  return (
    <div ref={ref} className="w-full" data-testid="lactate-chart">
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={merged} margin={{ top: 44, right: 56, left: 8, bottom: 28 }}>
          {zoneBands.map((b) => (
            <ReferenceArea
              key={b.key}
              x1={b.x1}
              x2={b.x2}
              y1={0}
              strokeOpacity={0}
              fill={b.color}
              fillOpacity={0.55}
              ifOverflow="extendDomain"
            />
          ))}
          <CartesianGrid stroke="#E7E5E4" strokeDasharray="2 4" vertical={false} />
          <XAxis
            type="number"
            dataKey="speed"
            domain={speedDomain}
            tickCount={8}
            stroke="#57534E"
            tick={{ fill: "#1C1917", fontSize: 12, fontFamily: "Montserrat" }}
            tickFormatter={(v) => v.toFixed(1)}
            allowDuplicatedCategory={false}
          >
            <Label
              value={`Speed (${speed_unit.toUpperCase()})`}
              position="insideBottom"
              offset={-14}
              style={{
                fill: "#1C1917",
                fontFamily: "Montserrat",
                fontSize: 12,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
              }}
            />
          </XAxis>
          <YAxis
            yAxisId="lactate"
            orientation="left"
            stroke="#7A1F2B"
            tick={{ fill: "#7A1F2B", fontSize: 12, fontFamily: "Montserrat" }}
            domain={[0, (dataMax) => Math.max(10, Math.ceil((dataMax || 0) + 1))]}
          >
            <Label
              angle={-90}
              value="Blood Lactate (mmol/L)"
              position="insideLeft"
              offset={10}
              style={{
                fill: "#7A1F2B",
                fontFamily: "Montserrat",
                fontSize: 12,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                textAnchor: "middle",
              }}
            />
          </YAxis>
          <YAxis
            yAxisId="hr"
            orientation="right"
            stroke="#1E3A8A"
            tick={{ fill: "#1E3A8A", fontSize: 12, fontFamily: "Montserrat" }}
            domain={[80, (dataMax) => Math.max(200, Math.ceil((dataMax || 0) + 10))]}
          >
            <Label
              angle={90}
              value="Heart Rate (bpm)"
              position="insideRight"
              offset={10}
              style={{
                fill: "#1E3A8A",
                fontFamily: "Montserrat",
                fontSize: 12,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                textAnchor: "middle",
              }}
            />
          </YAxis>
          <Tooltip
            contentStyle={{
              background: "#fff",
              border: "1px solid #1C1917",
              borderRadius: 0,
              fontFamily: "Montserrat",
            }}
            labelFormatter={(v) => `Speed: ${Number(v).toFixed(2)} ${speed_unit}`}
            formatter={(value, name) => {
              if (name === "lactate") return [`${value} mmol/L`, "Blood Lactate"];
              if (name === "hr") return [`${value} bpm`, "Heart Rate"];
              if (name === "lactate_fit")
                return [`${Number(value).toFixed(2)} mmol/L`, "Trend (fit)"];
              return [value, name];
            }}
          />
          {/* Polynomial trend line — rendered FIRST so it sits behind the actual curves */}
          <Line
            yAxisId="lactate"
            type="monotone"
            dataKey="lactate_fit"
            stroke="#7A1F2B"
            strokeWidth={2}
            strokeOpacity={0.22}
            dot={false}
            activeDot={false}
            connectNulls
            isAnimationActive={false}
            legendType="none"
          />
          <Line
            yAxisId="hr"
            type="monotone"
            dataKey="hr"
            stroke="#1E3A8A"
            strokeWidth={2}
            dot={{ r: 3.5, fill: "#1E3A8A", strokeWidth: 0 }}
            activeDot={{ r: 5 }}
            connectNulls
            isAnimationActive={false}
          />
          <Line
            yAxisId="lactate"
            type="monotone"
            dataKey="lactate"
            stroke="#7A1F2B"
            strokeWidth={2.5}
            dot={{ r: 4, fill: "#7A1F2B", strokeWidth: 0 }}
            activeDot={{ r: 6 }}
            connectNulls
            isAnimationActive={false}
          />
          {lt1Speed != null && (
            <ReferenceLine
              yAxisId="lactate"
              x={lt1Speed}
              stroke="#1C1917"
              strokeDasharray="4 4"
              strokeWidth={1.5}
            >
              <Label
                value={`LT1 · ${lt1Speed} ${speed_unit}`}
                position="insideTopLeft"
                offset={6}
                style={{
                  fill: "#1C1917",
                  fontFamily: "Montserrat",
                  fontSize: 11,
                  fontWeight: 600,
                }}
              />
            </ReferenceLine>
          )}
          {lt2Speed != null && (
            <ReferenceLine
              yAxisId="lactate"
              x={lt2Speed}
              stroke="#7A1F2B"
              strokeDasharray="4 4"
              strokeWidth={1.75}
            >
              <Label
                value={`LT2 · ${lt2Speed} ${speed_unit}`}
                position="insideTopLeft"
                offset={6}
                style={{
                  fill: "#7A1F2B",
                  fontFamily: "Montserrat",
                  fontSize: 11,
                  fontWeight: 700,
                }}
              />
            </ReferenceLine>
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
});

export default LactateChart;
